import React, { useState, useEffect, useCallback } from 'react';
import {
  IconMenu2, IconShield, IconLock, IconUsers, IconDeviceFloppy,
  IconChevronRight, IconLayoutDashboard, IconMail, IconLayoutList,
  IconCalendarEvent, IconHistory, IconSpeakerphone, IconFolder,
  IconKey, IconShieldLock, IconDatabaseExport, IconSettings,
  IconAdjustments, IconGolf, IconChevronDown,
} from '@tabler/icons-react';
import api from '../api/api';

/* 아이콘 코드 → 컴포넌트 매핑 */
const ICON_MAP = {
  IconLayoutDashboard, IconUsers, IconShield, IconMenu2, IconMail,
  IconLayoutList, IconCalendarEvent, IconHistory, IconSpeakerphone,
  IconFolder, IconKey, IconShieldLock, IconDatabaseExport, IconSettings,
  IconAdjustments, IconGolf, IconLock,
};

export default function MenuPage({ me, showToast }) {
  const [roles, setRoles]             = useState([]);
  const [allMenus, setAllMenus]       = useState([]);
  const [menuGroups, setMenuGroups]   = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [checkedMenus, setCheckedMenus] = useState(new Set());
  const [saving, setSaving]           = useState(false);
  const [dirty, setDirty]             = useState(false);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const isMaster = me?.role === 'MASTER';

  /* ── API 조회 ── */
  const fetchRoles = useCallback(async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data);
      return true;
    } catch (err) {
      const msg = `역할 목록 조회 실패 (${err.response?.status ?? '네트워크 오류'})`;
      setLoadError(msg);
      showToast(msg, 'danger');
      return false;
    }
  }, [showToast]);

  const fetchMenus = useCallback(async () => {
    try {
      const res = await api.get('/menus');
      setAllMenus(res.data);
    } catch (err) {
      showToast(`메뉴 목록 조회 실패 (${err.response?.status ?? '네트워크 오류'})`, 'danger');
    }
  }, [showToast]);

  const fetchMenuGroups = useCallback(async () => {
    try {
      const res = await api.get('/menus/groups');
      setMenuGroups(res.data);
    } catch (err) {
      showToast(`메뉴 그룹 조회 실패 (${err.response?.status ?? '네트워크 오류'})`, 'danger');
    }
  }, [showToast]);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    Promise.all([fetchRoles(), fetchMenus(), fetchMenuGroups()]).finally(() => setLoading(false));
  }, []);

  /* ── 역할 선택 ── */
  const handleSelectRole = (role) => {
    if (dirty && !window.confirm('저장되지 않은 변경사항이 있습니다. 이동하시겠습니까?')) return;
    setSelectedRole(role);
    setCheckedMenus(new Set((role.menus || []).map(m => m.id)));
    setDirty(false);
  };

  /* ── 체크박스 토글 ── */
  const toggleMenu = (menuId) => {
    if (!isMaster) return;
    setCheckedMenus(prev => {
      const next = new Set(prev);
      next.has(menuId) ? next.delete(menuId) : next.add(menuId);
      return next;
    });
    setDirty(true);
  };

  /* ── 저장 ── */
  const handleSave = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await api.put(`/menus/roles/${selectedRole.id}`, [...checkedMenus]);
      showToast('메뉴 설정이 저장되었습니다.');
      setDirty(false);
      await fetchRoles();
      setSelectedRole(prev => ({
        ...prev,
        menus: allMenus.filter(m => checkedMenus.has(m.id)),
      }));
    } catch {
      showToast('메뉴 저장 실패', 'danger');
    } finally {
      setSaving(false);
    }
  };

  /* ── 역할 뱃지 색상 ── */
  const roleBadgeColor = (code) => {
    if (code === 'MASTER') return 'bg-purple';
    if (code === 'ADMIN')  return 'bg-primary';
    return 'bg-azure';
  };

  /* ── 관리자 메뉴만 필터링 ── */
  const adminMenus = allMenus.filter(m => m.target === 'ADMIN' || !m.target);

  /* ── 그룹별 메뉴 정리 ── */
  const groupedMenus = menuGroups.map(group => ({
    ...group,
    menus: adminMenus
      .filter(m => m.groupCode === group.code)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
  })).filter(g => g.menus.length > 0);

  // groupCode가 없는 메뉴 (미분류)
  const ungroupedMenus = adminMenus.filter(m => !m.groupCode);

  const toggleGroupCollapse = (code) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  return (
    <div className="page-body">
      <div className="container-xl">

        {/* 상단 헤더 */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="text-muted small">메뉴 관리</div>
            <h2 className="mb-0" style={{ fontSize: 20, fontWeight: 700 }}>역할별 메뉴 설정</h2>
          </div>
        </div>

        {/* 로딩 */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" style={{ width: 40, height: 40 }} />
            <div className="text-muted mt-2">데이터 로딩 중...</div>
          </div>
        )}

        {/* 오류 */}
        {!loading && loadError && (
          <div className="alert alert-danger d-flex align-items-center gap-2">
            <IconMenu2 size={20} />
            <div><strong>조회 실패</strong><br /><small>{loadError}</small></div>
          </div>
        )}

        {/* 본문 */}
        {!loading && !loadError && (
          <div className="row g-3">

            {/* ── 좌측: 역할 목록 ── */}
            <div className="col-md-4 col-lg-3">
              <div className="card h-100">
                <div className="card-header">
                  <h4 className="card-title mb-0 d-flex align-items-center gap-2">
                    <IconShield size={18} /> 역할 목록
                  </h4>
                </div>
                <div className="list-group list-group-flush">
                  {roles.length === 0 && (
                    <div className="list-group-item text-muted text-center py-4">역할 없음</div>
                  )}
                  {roles.filter(r => r.code !== 'USER').map(role => {
                    const isActive = selectedRole?.id === role.id;
                    return (
                      <div
                        key={role.id}
                        className={`list-group-item list-group-item-action${isActive ? ' active' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelectRole(role)}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className={`badge ${roleBadgeColor(role.code)} text-white`}>
                                {role.code}
                              </span>
                              {role.isSystem && (
                                <IconLock size={12} className={isActive ? 'text-white' : 'text-muted'} />
                              )}
                            </div>
                            <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>
                              {role.name}
                            </div>
                            <div className={`small ${isActive ? 'text-white opacity-75' : 'text-muted'}`}
                              style={{ fontSize: 11 }}>
                              <IconUsers size={11} className="me-1" />
                              {role.userCount ?? 0}명 · 메뉴 {role.menus?.length ?? 0}개
                            </div>
                          </div>
                          <IconChevronRight size={14} className={isActive ? 'text-white' : 'text-muted'} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── 우측: 메뉴 체크박스 ── */}
            <div className="col-md-8 col-lg-9">
              {!selectedRole ? (
                <div className="card h-100 d-flex align-items-center justify-content-center"
                  style={{ minHeight: 300 }}>
                  <div className="text-center text-muted py-5">
                    <IconMenu2 size={48} className="mb-3 opacity-25" />
                    <div>좌측에서 역할을 선택하세요</div>
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <div>
                      <h4 className="card-title mb-0 d-flex align-items-center gap-2">
                        <span className={`badge ${roleBadgeColor(selectedRole.code)} text-white`}>
                          {selectedRole.code}
                        </span>
                        {selectedRole.name}
                      </h4>
                      <div className="text-muted small mt-1">
                        이 역할이 볼 수 있는 메뉴를 선택하세요
                      </div>
                    </div>
                    {isMaster && (
                      <button
                        className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                        onClick={handleSave}
                        disabled={saving || !dirty}
                      >
                        {saving
                          ? <span className="spinner-border spinner-border-sm" />
                          : <IconDeviceFloppy size={16} />}
                        저장
                      </button>
                    )}
                  </div>

                  <div className="card-body">
                    {!isMaster && (
                      <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                        <IconLock size={14} className="me-1" />
                        MASTER 계정만 메뉴 설정을 변경할 수 있습니다.
                      </div>
                    )}

                    {/* MASTER 역할은 항상 전체 메뉴 접근 안내 */}
                    {selectedRole.code === 'MASTER' && (
                      <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                        MASTER 역할은 항상 모든 메뉴에 접근할 수 있습니다. (설정 무관)
                      </div>
                    )}

                    {groupedMenus.map(group => {
                      const GroupIcon = ICON_MAP[group.icon] || IconMenu2;
                      const isCollapsed = collapsedGroups.has(group.code);
                      return (
                        <div key={group.code} className="mb-4">
                          {/* 그룹 헤더 */}
                          <div
                            className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom"
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleGroupCollapse(group.code)}
                          >
                            <GroupIcon size={18} className="text-primary" />
                            <h4 className="mb-0" style={{ fontSize: 15, fontWeight: 700 }}>
                              {group.name}
                            </h4>
                            <span className="badge bg-secondary-lt text-muted ms-1" style={{ fontSize: 11 }}>
                              {group.menus.length}개
                            </span>
                            <IconChevronDown
                              size={14}
                              className="text-muted ms-auto"
                              style={{
                                transition: 'transform 0.2s',
                                transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                              }}
                            />
                          </div>
                          {/* 그룹 메뉴 목록 */}
                          {!isCollapsed && (
                            <div className="row g-3">
                              {group.menus.map(menu => {
                                const checked = checkedMenus.has(menu.id);
                                const MenuIcon = ICON_MAP[menu.icon] || IconMenu2;
                                return (
                                  <div key={menu.id} className="col-sm-6 col-lg-4">
                                    <label
                                      className={`d-flex align-items-center gap-3 p-3 rounded border
                                        ${checked
                                          ? 'border-primary bg-primary-lt'
                                          : 'border-secondary-subtle'}`}
                                      style={{
                                        cursor: isMaster ? 'pointer' : 'default',
                                        transition: 'all 0.15s',
                                      }}
                                      onClick={(e) => { e.preventDefault(); toggleMenu(menu.id); }}
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input mt-0 flex-shrink-0"
                                        checked={checked}
                                        onChange={() => {}}
                                        disabled={!isMaster}
                                        style={{ pointerEvents: 'none' }}
                                      />
                                      <MenuIcon size={20} className={checked ? 'text-primary' : 'text-muted'} />
                                      <div>
                                        <div className="fw-semibold" style={{ fontSize: 14 }}>
                                          {menu.name}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: 11 }}>
                                          {menu.code}
                                        </div>
                                      </div>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* 미분류 메뉴 */}
                    {ungroupedMenus.length > 0 && (
                      <div className="mb-4">
                        <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                          <IconMenu2 size={18} className="text-muted" />
                          <h4 className="mb-0" style={{ fontSize: 15, fontWeight: 700 }}>
                            미분류
                          </h4>
                          <span className="badge bg-secondary-lt text-muted ms-1" style={{ fontSize: 11 }}>
                            {ungroupedMenus.length}개
                          </span>
                        </div>
                        <div className="row g-3">
                          {ungroupedMenus.map(menu => {
                            const checked = checkedMenus.has(menu.id);
                            const MenuIcon = ICON_MAP[menu.icon] || IconMenu2;
                            return (
                              <div key={menu.id} className="col-sm-6 col-lg-4">
                                <label
                                  className={`d-flex align-items-center gap-3 p-3 rounded border
                                    ${checked
                                      ? 'border-primary bg-primary-lt'
                                      : 'border-secondary-subtle'}`}
                                  style={{
                                    cursor: isMaster ? 'pointer' : 'default',
                                    transition: 'all 0.15s',
                                  }}
                                  onClick={(e) => { e.preventDefault(); toggleMenu(menu.id); }}
                                >
                                  <input
                                    type="checkbox"
                                    className="form-check-input mt-0 flex-shrink-0"
                                    checked={checked}
                                    onChange={() => {}}
                                    disabled={!isMaster}
                                    style={{ pointerEvents: 'none' }}
                                  />
                                  <MenuIcon size={20} className={checked ? 'text-primary' : 'text-muted'} />
                                  <div>
                                    <div className="fw-semibold" style={{ fontSize: 14 }}>
                                      {menu.name}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: 11 }}>
                                      {menu.code}
                                    </div>
                                  </div>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
